import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { AutoComplete, Input } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  buildAutocompleteRequest,
  placeToFormValue,
  predictionLabel,
  toPlacesSelection,
  type GooglePlacesMode,
  type GooglePlacesSelection,
} from '@/components/form/shared/googlePlacesHelpers';

import type { SizeType } from 'antd/es/config-provider/SizeContext';
import type React from 'react';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

type GooglePlacesInputProps = {
  value?: string;
  /** Second arg is only set when the user picks a Places suggestion. */
  onChange?: (value: string, place?: GooglePlacesSelection) => void;
  /**
   * Called when the user selects a Places suggestion, with the full place
   * metadata (including country_code).  Use this instead of parsing the
   * second argument of `onChange` when you need structured place data without
   * interfering with Ant Design Form.Item's onChange injection.
   */
  onPlaceSelect?: (place: GooglePlacesSelection) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: SizeType;
  /** Form.Item injects this for label association / Cypress selectors. */
  id?: string;
  /** Default `cities`. Use `address` for street / office locations. */
  mode?: GooglePlacesMode;
};

function debounceStringCallback(func: (value: string) => void, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const debounced = (value: string) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      timeout = undefined;
      func(value);
    }, wait);
  };
  debounced.flush = (value: string) => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
    func(value);
  };
  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = undefined;
    }
  };
  return debounced;
}

type SuggestionOption = {
  value: string;
  label: string;
};

type GooglePlacesInputFieldProps = GooglePlacesInputProps & {
  placesLibrary: google.maps.PlacesLibrary | null;
};

const GooglePlacesInputField: React.FC<GooglePlacesInputFieldProps> = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder,
  className,
  disabled = false,
  size,
  id,
  mode = 'cities',
  placesLibrary,
}) => {
  const [displayValue, setDisplayValue] = useState(value ?? '');
  const [options, setOptions] = useState<SuggestionOption[]>([]);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const predictionByKeyRef = useRef<
    Map<string, google.maps.places.PlacePrediction>
  >(new Map());
  const requestIdRef = useRef(0);

  useEffect(() => {
    setDisplayValue(value ?? '');
  }, [value]);

  const ensureSessionToken = () => {
    if (!placesLibrary) {
      return null;
    }
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const resetSessionToken = () => {
    sessionTokenRef.current = null;
  };

  const emitFreeText = useMemo(
    () =>
      debounceStringCallback((next: string) => {
        onChange?.(next);
      }, 300),
    [onChange],
  );

  const fetchSuggestions = useMemo(
    () =>
      debounceStringCallback((input: string) => {
        void (async () => {
          if (!placesLibrary || disabled || !input.trim()) {
            setOptions([]);
            predictionByKeyRef.current.clear();
            return;
          }
          const token = ensureSessionToken();
          if (!token) {
            return;
          }
          const requestId = ++requestIdRef.current;
          try {
            const { suggestions } =
              await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                buildAutocompleteRequest(input, mode, token),
              );
            if (requestId !== requestIdRef.current) {
              return;
            }
            const nextOptions: SuggestionOption[] = [];
            const nextMap = new Map<
              string,
              google.maps.places.PlacePrediction
            >();
            for (const suggestion of suggestions) {
              const prediction = suggestion.placePrediction;
              if (!prediction) {
                continue;
              }
              const label = predictionLabel(prediction);
              if (!label) {
                continue;
              }
              const key = prediction.placeId || label;
              nextMap.set(key, prediction);
              nextOptions.push({ value: key, label });
            }
            predictionByKeyRef.current = nextMap;
            setOptions(nextOptions);
          } catch {
            if (requestId === requestIdRef.current) {
              setOptions([]);
              predictionByKeyRef.current.clear();
            }
          }
        })();
      }, 250),
    // Session token helper closes over placesLibrary; deps cover fetch drivers.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- placesLibrary/mode/disabled
    [disabled, mode, placesLibrary],
  );

  useEffect(() => {
    return () => {
      emitFreeText.cancel();
      fetchSuggestions.cancel();
    };
  }, [emitFreeText, fetchSuggestions]);

  const handleSelect = (key: string) => {
    // Ant Design fires onChange(key) before onSelect(key), where `key` is the
    // internal placeId string (e.g. "ChIJN1t_tDeuEmsRUsoyG83frY4").  That
    // onChange call sets displayValue to the raw placeId and schedules
    // emitFreeText / fetchSuggestions — all of which we need to cancel/override
    // synchronously here before the async fetch begins.
    //
    // Use prediction.mainText (just the city name, e.g. "Amsterdam") rather
    // than the full label ("Amsterdam, Netherlands") so there is no visible
    // transition once fetchFields resolves — placeToFormValue returns the same
    // displayName value.
    const prediction = predictionByKeyRef.current.get(key);
    const immediateLabel =
      prediction?.mainText?.toString() ??
      options.find((option) => option.value === key)?.label ??
      key;
    setDisplayValue(immediateLabel); // overrides placeId that onChange just set
    emitFreeText.cancel();
    fetchSuggestions.cancel();

    void (async () => {
      if (!prediction || !placesLibrary) {
        onChange?.(immediateLabel);
        setOptions([]);
        return;
      }

      try {
        const place = prediction.toPlace();
        await place.fetchFields({
          fields: [
            'displayName',
            'formattedAddress',
            'id',
            'location',
            'addressComponents',
          ],
        });
        const nextValue = placeToFormValue(place);
        if (!nextValue) {
          return;
        }
        setDisplayValue(nextValue);
        const selection = toPlacesSelection(place);
        onChange?.(nextValue, selection);
        onPlaceSelect?.(selection);
        resetSessionToken();
      } catch {
        const label = predictionLabel(prediction);
        setDisplayValue(label);
        onChange?.(label);
      } finally {
        setOptions([]);
        predictionByKeyRef.current.clear();
      }
    })();
  };

  const handleBlur = () => {
    emitFreeText.flush(displayValue);
  };

  return (
    <AutoComplete
      className={className}
      value={displayValue}
      options={options}
      onChange={(next) => {
        const text = typeof next === 'string' ? next : String(next ?? '');
        setDisplayValue(text);
        emitFreeText(text);
        fetchSuggestions(text);
      }}
      onSelect={handleSelect}
      onBlur={handleBlur}
      disabled={disabled}
      showSearch={{ filterOption: false }}
      defaultActiveFirstOption={false}
      allowClear
      onClear={() => {
        setDisplayValue('');
        setOptions([]);
        predictionByKeyRef.current.clear();
        emitFreeText.cancel();
        onChange?.('');
        resetSessionToken();
      }}
    >
      <Input
        id={id}
        size={size}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
    </AutoComplete>
  );
};

const GooglePlacesInputWithLibrary: React.FC<GooglePlacesInputProps> = (
  props,
) => {
  const placesLibrary = useMapsLibrary('places');
  return <GooglePlacesInputField {...props} placesLibrary={placesLibrary} />;
};

const GooglePlacesInput: React.FC<GooglePlacesInputProps> = (props) => {
  if (!GOOGLE_MAPS_API_KEY) {
    return <GooglePlacesInputField {...props} placesLibrary={null} />;
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <GooglePlacesInputWithLibrary {...props} />
    </APIProvider>
  );
};

export default GooglePlacesInput;
